import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { permissionsAPI } from '../api/permissionsAPI';

export const PermissionContext = createContext({
    permissions: [],
    permissionCodes: [],
    isSuperuser: false,
    isPermissionAdmin: false,
    loading: true,
    error: null,
    hasPermission: () => false,
    hasAnyPermission: () => false,
    hasAllPermissions: () => false,
    refreshPermissions: () => {},
});

/**
 * Hook to access permission context
 */
export const usePermissions = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermissions must be used within a PermissionProvider');
    }
    return context;
};

export const PermissionProvider = ({ children }) => {
    const [permissions, setPermissions] = useState([]);
    const [permissionCodes, setPermissionCodes] = useState([]);
    const [isSuperuser, setIsSuperuser] = useState(false);
    const [isPermissionAdmin, setIsPermissionAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Load current user's permissions from the API
     */
    const loadPermissions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await permissionsAPI.getCurrentUserPermissions();

            // data format: { permissions: ['inventory.view', 'po.modify', ...], is_superuser: boolean, is_permission_admin: boolean }
            const permissionCodes = data.permissions || [];

            setPermissions(permissionCodes);
            setPermissionCodes(permissionCodes);
            setIsSuperuser(data.is_superuser || false);
            setIsPermissionAdmin(data.is_permission_admin || false);
        } catch (err) {
            console.error('Failed to load permissions:', err);
            setError(err.message || 'Failed to load permissions');
            setPermissions([]);
            setPermissionCodes([]);
            setIsSuperuser(false);
            setIsPermissionAdmin(false);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Load permissions on mount
     */
    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    /**
     * Check if user has a specific permission code
     * @param {string} code - Permission code to check (e.g., 'inventory.view')
     * @returns {boolean}
     */
    const hasPermission = useCallback((code) => {
        if (!code) return false;
        // Superusers have all permissions
        if (isSuperuser) return true;
        return permissionCodes.includes(code);
    }, [permissionCodes, isSuperuser]);

    /**
     * Check if user has at least one of the specified permission codes
     * @param {string[]} codes - Array of permission codes
     * @returns {boolean}
     */
    const hasAnyPermission = useCallback((codes) => {
        if (!codes || codes.length === 0) return false;
        // Superusers have all permissions
        if (isSuperuser) return true;
        return codes.some(code => permissionCodes.includes(code));
    }, [permissionCodes, isSuperuser]);

    /**
     * Check if user has all of the specified permission codes
     * @param {string[]} codes - Array of permission codes
     * @returns {boolean}
     */
    const hasAllPermissions = useCallback((codes) => {
        if (!codes || codes.length === 0) return true;
        // Superusers have all permissions
        if (isSuperuser) return true;
        return codes.every(code => permissionCodes.includes(code));
    }, [permissionCodes, isSuperuser]);

    const value = {
        permissions,
        permissionCodes,
        isSuperuser,
        isPermissionAdmin,
        loading,
        error,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refreshPermissions: loadPermissions,
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};
